import React from 'react';
import {
  IconAlertTriangle,
  IconClockHour4,
  IconLink,
  IconCircleCheck,
} from '@tabler/icons-react';

import BusinessName from '../BusinessName';
import {
  DataDetailCheckout,
  SettingsProps,
  CheckoutStateValue,
} from '@pages/types';
import { useCheckoutStep } from '@pages/hooks/useCheckoutStep';

interface AccountBindingStateProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  onRefreshData: () => Promise<unknown>;
}

const AccountBindingState: React.FC<AccountBindingStateProps> = ({
  data,
  settings,
  onRefreshData,
}) => {
  const { goToMethod } = useCheckoutStep();

  const checkoutState = data?.checkout_state as CheckoutStateValue | undefined;
  const accountBindingStatus = data?.account_binding?.status;

  const stateConfig: Record<
    | 'ACCOUNT_UNBOUND_SUCCESS'
    | 'ACCOUNT_BINDING_REQUIRED'
    | 'ACCOUNT_BINDING_PROGRESS'
    | 'ACCOUNT_BINDING_FAILED',
    {
      title: string;
      subtitle: string;
      description: string;
      Icon: React.ComponentType<{ className?: string }>;
      primaryLabel: string;
      primaryAction: () => void;
      secondaryLabel?: string;
      secondaryAction?: () => void;
    }
  > = {
    ACCOUNT_UNBOUND_SUCCESS: {
      title: 'Account Successfully Unbound',
      subtitle: 'Your account has been successfully unbound',
      description:
        'Akun Anda berhasil di-unbind. Silakan pilih metode pembayaran kembali jika ingin melanjutkan proses pembayaran.',
      Icon: IconCircleCheck,
      primaryLabel: 'Choose Payment Method',
      primaryAction: goToMethod,
      secondaryLabel: 'Refresh Status',
      secondaryAction: () => {
        void onRefreshData();
      },
    },
    ACCOUNT_BINDING_REQUIRED: {
      title: 'Account Binding Required',
      subtitle: 'Bind your account before continuing payment',
      description:
        'Untuk metode ini, akun Anda perlu dihubungkan terlebih dahulu agar pembayaran dapat diproses.',
      Icon: IconLink,
      primaryLabel: 'Go to Payment Method',
      primaryAction: goToMethod,
    },
    ACCOUNT_BINDING_PROGRESS: {
      title: 'Account Binding in Progress',
      subtitle: 'Your account binding is being processed',
      description:
        'Proses binding sedang berjalan. Silakan cek status secara berkala untuk melanjutkan ke tahap pembayaran.',
      Icon: IconClockHour4,
      primaryLabel: 'Check Status',
      primaryAction: () => {
        void onRefreshData();
      },
      secondaryLabel: 'Change Payment Method',
      secondaryAction: goToMethod,
    },
    ACCOUNT_BINDING_FAILED: {
      title: 'Account Binding Failed',
      subtitle: 'We could not bind your account',
      description:
        'Binding akun gagal. Anda bisa mencoba lagi atau memilih metode pembayaran lain.',
      Icon: IconAlertTriangle,
      primaryLabel: 'Try Again',
      primaryAction: goToMethod,
      secondaryLabel: 'Refresh Status',
      secondaryAction: () => {
        void onRefreshData();
      },
    },
  };

  const key =
    accountBindingStatus === 'UNBOUND'
      ? 'ACCOUNT_UNBOUND_SUCCESS'
      : checkoutState === 'ACCOUNT_BINDING_PROGRESS'
        ? 'ACCOUNT_BINDING_PROGRESS'
        : checkoutState === 'ACCOUNT_BINDING_FAILED'
          ? 'ACCOUNT_BINDING_FAILED'
          : 'ACCOUNT_BINDING_REQUIRED';

  const config = stateConfig[key];

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <BusinessName
        data={data}
        title={config.title}
        subtitle={config.subtitle}
      />

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <config.Icon className="h-7 w-7 text-gray-700" />
          <span className="text-sm font-medium text-gray-500">
            {accountBindingStatus === 'UNBOUND' ? 'UNBOUND' : key}
          </span>
        </div>

        <p className="text-sm text-gray-700">{config.description}</p>

        <div className="mt-6 flex flex-col gap-3 md:flex-row">
          <button
            type="button"
            onClick={config.primaryAction}
            className="w-full rounded-lg px-4 py-3 font-semibold transition-opacity hover:opacity-90"
            style={{
              backgroundColor: settings.button_color || '#5D87FF',
              color: settings.button_text_color || '#FFFFFF',
            }}
          >
            {config.primaryLabel}
          </button>

          {config.secondaryLabel && config.secondaryAction && (
            <button
              type="button"
              onClick={config.secondaryAction}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 font-semibold text-gray-800 transition-opacity hover:opacity-90"
            >
              {config.secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountBindingState;
